import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useApiFormAuthDevice } from '../../../api-new/state/auth'
import { useModalActions } from '../../../shared/component/modal-dialog'
import { IconPaste } from '../../../shared/icon/icon'
import { useDeviceCode, codeFromClipboard } from './use-device-code'
import { CodeWells } from './code-wells'
import { Numberpad } from './numberpad'
import { CheckingView } from './checking-view'
import { ConfirmView } from './confirm-view'
import { SuccessView } from './success-view'
import { ExpiredView } from './expired-view'
import './login-device.scss'
import { LookupNotice } from './lookup-notice'

export const AccessPinModal = ({ target }) => {
  const api = useApiFormAuthDevice()
  const modalActions = useModalActions(target)
  const { state, pressDigit, backspace, reject, approve, reset } =
    useDeviceCode({ api, onClose: () => modalActions.hide() })

  const phaseRef = useRef(state.phase)
  phaseRef.current = state.phase

  // The header mounts this modal once, closed, on every signed-in page, so
  // anything that listens outside the modal must check it is actually open.
  // The focus auto-paste below did not: switching back to the browser with a
  // device code on the clipboard typed that code into the hidden modal and
  // looked it up. Worse, the focus that comes from clicking into the window
  // lands BEFORE the click opens the modal, so a read started then resolved
  // after the open had reset the wells -- and a stale, nearly expired code
  // appeared the moment the modal opened.
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  // Shown: the open animation is over, so the dialog's place can be measured.
  const [shown, setShown] = useState(false)
  const dialogRef = useRef(null)

  // Leaving a sign-in request you were shown, without approving it, is a denial.
  // Held in a ref because the dismiss listener below is bound once and would
  // otherwise close over a stale reject.
  const rejectRef = useRef(reject)
  rejectRef.current = reject
  const denyIfPending = () => { if (phaseRef.current === 'confirm') rejectRef.current('dismissed') }

  // Paste a code from the clipboard (touch-friendly alternative to Cmd+V / typing).
  // Browsers gate clipboard reads behind a user gesture, so we can only validate on
  // click, not pre-check. If the clipboard has no valid code, flash a brief hint.
  const [pasteHint, setPasteHint] = useState(false)

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      const code = codeFromClipboard(text, state.codeLength)
      if (code) {
        code.split('').forEach(d => pressDigit(d))
        return
      }
    } catch (e) {
      // clipboard read blocked (permissions / insecure context) — fall through to hint
    }
    setPasteHint(true)
    setTimeout(() => setPasteHint(false), 1800)
  }

  // Reset on open; block dismissal (backdrop tap / ESC) while checking.
  useEffect(() => {
    const ele = document.getElementById(target)
    if (!ele) return
    const onShow = () => { reset(); openRef.current = true; setOpen(true) }
    const onShown = () => setShown(true)
    const onHide = e => {
      if (phaseRef.current === 'checking') { e.preventDefault(); return }
      // Covers ESC and a backdrop tap as well as the X.
      denyIfPending()
    }
    const onHidden = () => { openRef.current = false; setOpen(false); setShown(false) }
    ele.addEventListener('show.bs.modal', onShow)
    ele.addEventListener('shown.bs.modal', onShown)
    ele.addEventListener('hide.bs.modal', onHide)
    ele.addEventListener('hidden.bs.modal', onHidden)
    return () => {
      ele.removeEventListener('show.bs.modal', onShow)
      ele.removeEventListener('shown.bs.modal', onShown)
      ele.removeEventListener('hide.bs.modal', onHide)
      ele.removeEventListener('hidden.bs.modal', onHidden)
    }
  }, [target, reset])

  // Best-effort auto-paste: when the window regains focus (e.g. after copying the code
  // on the other device and switching back) and the wells are empty, try to read a
  // code of the server's advertised length from the clipboard and fill it. Works
  // where the browser allows a
  // permissioned read (Chrome); Safari blocks silent reads, so it no-ops there and the
  // Paste button / typing still work. Scoped to entry+empty so it never reads at random.
  useEffect(() => {
    if (!open || state.phase !== 'entry') return
    const tryAutoPaste = async () => {
      if (state.digits.length > 0 || state.locked || state.erroring) return
      try {
        const text = await navigator.clipboard.readText()
        // The modal may have closed (and reopened fresh) while the read was
        // pending; a code from before that must not land in it.
        if (!openRef.current) return
        const code = codeFromClipboard(text, state.codeLength)
        if (code) code.split('').forEach(d => pressDigit(d))
      } catch (e) {
        // clipboard read blocked (Safari / no permission) — ignore
      }
    }
    window.addEventListener('focus', tryAutoPaste)
    return () => window.removeEventListener('focus', tryAutoPaste)
    // codeLength included: pasteable() closes over it, and a listener bound
    // before the settings call returns would otherwise keep testing against the
    // default length for the life of the modal.
  }, [open, state.phase, state.digits, state.locked, state.erroring, state.codeLength, pressDigit])

  const { phase } = state

  // The notice card under the buttons grows the modal at the bottom only. A
  // centred dialog re-centres whenever its height changes -- the card coming
  // and going, the keypad giving way to the spinner while a code is checked
  // -- and everything above moves with it. So while a code is typed or
  // checked the dialog is held where it was centred (its content's top, as
  // a margin on a top-aligned dialog), and centred again for the confirm,
  // success and expired screens. Only when the grown dialog would run off the
  // bottom of a short screen does it rise, by just enough to keep the card in
  // view, and it stays there (never bobbing back down as the card clears).
  // Re-measured on a resize.
  const entering = shown && (phase === 'entry' || phase === 'checking')
  useLayoutEffect(() => {
    const dialog = dialogRef.current
    const content = dialog && dialog.firstElementChild
    if (!dialog || !content) return
    const release = () => { dialog.style.removeProperty('align-items'); content.style.removeProperty('margin-top') }
    let centred = 0
    const place = () => {
      const margin = parseFloat(window.getComputedStyle(dialog).marginBottom) || 0
      const room = window.innerHeight - 2 * margin - content.getBoundingClientRect().height
      centred = Math.max(0, Math.min(centred, room))
      content.style.marginTop = `${Math.round(centred)}px`
    }
    const hold = () => {
      release()
      centred = content.getBoundingClientRect().top - dialog.getBoundingClientRect().top
      dialog.style.alignItems = 'flex-start'
      place()
    }
    if (!entering) { release(); return }
    hold()
    window.addEventListener('resize', hold)
    const grows = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(place) : null
    if (grows) grows.observe(content)
    return () => {
      window.removeEventListener('resize', hold)
      if (grows) grows.disconnect()
    }
  }, [entering])
  // Wells stay mounted across entry/checking/confirm so the error shake replays on
  // a failed lookup (the component isn't remounted) and the confirm screen shows the
  // entered code — mirroring the viewer's V2 layout.
  const showWells = phase === 'entry' || phase === 'checking' || phase === 'confirm'

  return (
    <div id={target} className="modal fade ld-modal-host" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby={phase === 'entry' ? `${target}-title` : undefined} aria-label={phase === 'entry' ? undefined : 'Sign in another device'}>
      <div className="modal-dialog modal-dialog-centered ld-dialog" ref={dialogRef}>
        <div className="modal-content ld-modal">
          <div className="ld-body">
            {phase === 'entry' &&
              <div className="ld-head">
                <div className="ld-head__title" id={`${target}-title`}>Sign in another device</div>
                <div className="ld-head__subtitle">Enter the code shown on the device you want to sign in.</div>
              </div>}

            {showWells && <CodeWells digits={state.digits} length={state.codeLength} shakeKey={state.shake} focusKey={state.retry} />}

            {phase === 'entry' && <Numberpad onDigit={pressDigit} onBackspace={backspace} codeLength={state.codeLength} />}
            {/* Real Bootstrap buttons (btn-primary / btn-secondary), matching the
                Approve/Deny and Retry/Close pairs the confirm and expired phases
                of this same modal already use via .ld-actions — these used to be
                bespoke `ld-paste`/`ld-cancel` classes styled with no border or
                fill at rest, so they rendered as plain text next to an icon. */}
            {phase === 'entry' &&
              <div className="ld-actions mt-3">
                <button type="button" className="btn btn-secondary text-capitalize" onClick={() => modalActions.hide()}>
                  Cancel
                </button>
                {/* A labelled Cancel instead of a close glyph, matching the viewer.
                    A bare X in the corner is the weakest affordance on the card:
                    smallest target, furthest from the pointer, and unlabelled in a
                    modal whose every other action is a labelled button. The scrim
                    and ESC still dismiss for anyone who already reaches for them,
                    and every exit denies the code either way — only the reason
                    differs. */}
                <button type="button" className={`btn ${pasteHint ? 'btn-outline-danger' : 'btn-primary'} text-capitalize`} onClick={handlePaste}>
                  <IconPaste /> {pasteHint ? `No ${state.codeLength}-digit code on clipboard` : 'Paste code'}
                </button>
              </div>}
            {/* What happened to the last code, in its own card under the
                buttons (lookup-notice.js), only when there is something to
                say: nothing sits between the boxes and the keypad. A live
                region that is always there (empty, it takes no room), so the
                card is announced; it clears with the next digit. */}
            {(phase === 'entry' || phase === 'checking') &&
              <div className="ld-notice-region" data-testid="ld-notice-region" aria-live="polite">
                {state.error && <LookupNotice message={state.error} failure={state.failure} fault={state.fault} />}
              </div>}
            {phase === 'checking' && <CheckingView />}
            {phase === 'confirm' &&
              <ConfirmView device={state.device} error={state.error} failure={state.failure} submitting={api.submitting} onReject={reject} onApprove={approve} />}
            {phase === 'success' && <SuccessView device={state.device} />}
            {/* Always mounted, so the approval is announced when its words
                arrive (a live region mounted already filled often isn't). */}
            <div className="visually-hidden" role="status" data-testid="ld-success-live">
              {phase === 'success' ? (state.device && state.device.label ? `Device signed in. ${state.device.label} is finishing signing in.` : 'Device signed in.') : ''}
            </div>
            {phase === 'expired' && <ExpiredView onClose={() => modalActions.hide()} onRetry={reset} />}
          </div>
        </div>
      </div>
    </div>
  )
}
